/**
 * Keyboard -> InputFrame adapter.
 *
 * Owns no game state. It only watches keydown/keyup on a target (default:
 * window) and emits an InputFrame on demand via `poll()`. The sim consumes
 * exactly what poll returns, never sees DOM events.
 *
 * Last-pressed-wins for direction: if the player presses Right then Up
 * without releasing Right, "Up" is what the sim sees. Release Up and
 * "Right" comes back. Mimics arcade joystick feel.
 */

import type { Direction, InputFrame } from '../sim/types.ts';

const DIR_FOR_KEY: Readonly<Record<string, Direction>> = {
  ArrowUp: 'N', KeyW: 'N',
  ArrowDown: 'S', KeyS: 'S',
  ArrowLeft: 'W', KeyA: 'W',
  ArrowRight: 'E', KeyD: 'E',
};

const SMOKE_KEYS = new Set(['Space', 'KeyJ', 'KeyZ']);
const START_KEYS = new Set(['Enter', 'NumpadEnter']);

export type InputAdapter = {
  poll(): InputFrame;
  dispose(): void;
};

export function attachKeyboard(target: Window | HTMLElement = window): InputAdapter {
  const heldDirs: Direction[] = []; // stack: top = last pressed
  let smokeQueued = false;
  let startQueued = false;

  function onKeyDown(ev: KeyboardEvent): void {
    if (ev.repeat) return; // ignore OS key-repeat
    const dir = DIR_FOR_KEY[ev.code];
    if (dir !== undefined) {
      // Remove existing occurrence then push to top.
      const i = heldDirs.indexOf(dir);
      if (i >= 0) heldDirs.splice(i, 1);
      heldDirs.push(dir);
      ev.preventDefault();
      return;
    }
    if (SMOKE_KEYS.has(ev.code)) {
      smokeQueued = true;
      ev.preventDefault();
      return;
    }
    if (START_KEYS.has(ev.code)) {
      startQueued = true;
      ev.preventDefault();
    }
  }

  function onKeyUp(ev: KeyboardEvent): void {
    const dir = DIR_FOR_KEY[ev.code];
    if (dir !== undefined) {
      const i = heldDirs.indexOf(dir);
      if (i >= 0) heldDirs.splice(i, 1);
      ev.preventDefault();
    }
  }

  target.addEventListener('keydown', onKeyDown as EventListener);
  target.addEventListener('keyup', onKeyUp as EventListener);

  return {
    poll(): InputFrame {
      const dir = heldDirs.length === 0 ? null : (heldDirs[heldDirs.length - 1] as Direction);
      const smoke = smokeQueued;
      const start = startQueued;
      smokeQueued = false;
      startQueued = false;
      return { dir, smoke, start };
    },
    dispose(): void {
      target.removeEventListener('keydown', onKeyDown as EventListener);
      target.removeEventListener('keyup', onKeyUp as EventListener);
    },
  };
}
